import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Create the authentication context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to check if token is expired
  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch (error) {
      return true; // If there's an error decoding, consider it expired
    }
  };

  // Check if token exists in localStorage on initial load and validate it
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken && !isTokenExpired(storedToken)) {
      setToken(storedToken);
      setIsAuthenticated(true);
      // Set authorization header for all future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    } else if (storedToken) {
      // Token exists but is expired
      logout(); // Clear the expired token
    }
    setLoading(false);
  }, []);

  // Add axios interceptor to handle token expiration during API calls
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // If we get a 401 Unauthorized error, the token might be expired
        if (error.response && error.response.status === 401) {
          logout(); // Clear the token and auth state
        }
        return Promise.reject(error);
      }
    );

    // Clean up interceptor on unmount
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Login function
  const login = async (username, password) => {
    setLoading(true);
    setError(null);
    try {
      // FormData is required for token endpoint
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await axios.post('/api/v1/token', formData);
      const { access_token } = response.data;
      
      // Save token to state and localStorage
      setToken(access_token);
      localStorage.setItem('token', access_token);
      
      // Set authorization header for all future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setIsAuthenticated(true);
      setLoading(false);
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
      setLoading(false);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
    // Remove authorization header
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        token,
        loading,
        error,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
